import { postJSON } from "../api.js";
import { esc } from "../ui/format.js";
import { uiEmpty, uiMoney, uiPays } from "../ui/kit.js";
import { toast } from "../ui/modals.js";

/**
 * Shared pieces for the release 1.6 systems (backend/systems.py): the
 * decision letters every system raises, one action helper, a log list and
 * two small 2D charts. The systems' own screens are workforce.js, esg.js,
 * rnd.js, ambassadors.js, league.js, wealth.js, career.js, timemachine.js
 * and takeover.js.
 */

/** Which screen answers each system's letters. */
const EM_HOME = {
  labor: ["workforce", "👷 Workforce"], esg: ["esg", "🌱 Climate & ESG"], rnd_lab: ["rnd", "🔬 R&D Lab"],
  sponsors: ["ambassadors", "⭐ Ambassadors"], founder: ["office", "💰 Wealth"], takeover: ["publicmarkets", "🦈 Public Markets"],
  restructuring: ["banking", "⚖️ Banking"],
  product_moves: ["products", "🧰 Products"], ops_moves: ["operations", "🧰 Operations"], partners: ["deals", "🧰 Deals"],
  ma_moves: ["ma", "🧰 M&A"], capital: ["banking", "🧰 Banking & markets"], gov_moves: ["government", "🧰 Government"],
  press: ["news", "🗞️ Press"], tax_planning: ["financials", "🧾 Tax"],
  // 2026-10-22 round
  rival_ceo: ["competitors", "🥊 Your rival"], contracts: ["deals", "📑 Contracts"], seasons: ["ambassadors", "📣 Marketing"],
  term_sheets: ["investors", "📄 Term sheets"], connections: ["government", "🏛 Regulators"], staffing: ["operations", "🧑‍💻 Engineers"],
  market_moves: ["publicmarkets", "📈 Markets"], lifestyle: ["lifestyle", "💎 Lifestyle"], dream: ["dream", "🏟️ Dream Projects"],
};

/** Run an action through the one action path, toast the result and refresh. */
async function emAct(type, params = {}, { refresh = true, toastMs } = {}) {
  try {
    const r = await postJSON("/actions/execute", { action: { type, params } });
    toast(r.message, false, toastMs);
    if (refresh && window.refreshAll) await window.refreshAll();
    return r;
  } catch (e) {
    toast(e.message || "That didn't work.", true, toastMs);
    return null;
  }
}

/** The letters waiting for an answer (decision-first: essentials and buttons, then the story). */
function emLetters(list, { empty = "" } = {}) {
  if (!list || !list.length) return empty ? uiEmpty("Nothing waiting", empty) : "";
  return list.map((d) => `
    <article class="nm-letter em-letter" data-em-letter="${d.id}">
      <div class="nm-letter-kick">${esc(d.who || "A letter")} · month ${d.month} · ${d.due_month > d.month ? `answer by the end of month ${d.due_month}` : "answer this month"}</div>
      <h3>${esc(d.title)}</h3>
      <div class="nm-options">${d.options.map((o) => `
        <button type="button" class="nm-opt" data-em-id="${d.id}" data-em-opt="${esc(o.key)}" ${o.affordable === false ? "disabled" : ""}>
          <b>${esc(o.label)}</b><span>${esc(o.detail)}</span>
          <em>${o.cost ? uiMoney(o.cost) : "No company cash"}${o.odds ? ` · ${o.odds}% odds` : ""}${o.affordable === false ? " · can't afford" : ""}</em>${uiPays(o)}</button>`).join("")}</div>
      <details class="em-story"><summary>The full story</summary>
        <p>${esc(d.body)}</p>${d.quote ? `<blockquote>“${esc(d.quote)}”<cite>${esc(d.who || "")}</cite></blockquote>` : ""}</details>
      <p class="nm-warn">${d.options.some((o) => o.key === d.default_key)
        ? `Leave it and the default happens: <b>${esc(d.options.find((o) => o.key === d.default_key).label)}</b>.`
        : "Leave it and it gets decided without you."}</p>
    </article>`).join("");
}

function emWireLetters(root, after) {
  root.querySelectorAll("[data-em-opt]").forEach((b) => b.addEventListener("click", async () => {
    root.querySelectorAll(`[data-em-id="${b.dataset.emId}"]`).forEach((x) => { x.disabled = true; });
    await emAct("decision_respond", { id: +b.dataset.emId, option: b.dataset.emOpt }, { refresh: !after });
    if (after) await after();
  }));
}

/** A short history list: [{month, text}] or decision history [{month, title, result}]. */
function emLog(items, { limit = 8, emptyText = "Nothing yet." } = {}) {
  if (!items || !items.length) return `<p class="sx-card-sub">${esc(emptyText)}</p>`;
  return `<ol class="em-log">${items.slice(0, limit).map((i) => `<li><b>M${i.month ?? "—"}</b><span>${esc(i.text || `${i.title}: ${i.result || ""}`)}</span></li>`).join("")}</ol>`;
}

/** A 2D line chart for a monthly series. series: [{month, value}], optional compare series (dashed). */
function emLine(series, { compare = null, height = 170, money = true, label = "", compareLabel = "", markers = [] } = {}) {
  const pts = (series || []).filter((p) => p.value !== null && p.value !== undefined);
  if (pts.length < 2) return `<div class="em-chart-empty">${esc(label || "The chart")} fills in as months close.</div>`;
  const all = pts.concat(compare || []);
  const W = 640, H = height, L = 58, R = 12, T = 14, B = 26;
  const m0 = Math.min(...all.map((p) => p.month)), m1 = Math.max(...all.map((p) => p.month));
  const v0 = Math.min(0, ...all.map((p) => p.value)), v1 = Math.max(1, ...all.map((p) => p.value));
  const x = (m) => L + ((W - L - R) * (m - m0)) / Math.max(1, m1 - m0);
  const y = (v) => T + ((H - T - B) * (v1 - v)) / Math.max(1e-9, v1 - v0);
  const path = (s) => s.map((p, i) => `${i ? "L" : "M"}${x(p.month).toFixed(1)},${y(p.value).toFixed(1)}`).join("");
  const fmt = (v) => (money ? uiMoney(v, { compact: true }) : `${Math.round(v)}`);
  const ticks = [v0, (v0 + v1) / 2, v1];
  const area = `${path(pts)}L${x(pts[pts.length - 1].month).toFixed(1)},${y(Math.max(v0, 0)).toFixed(1)}L${x(pts[0].month).toFixed(1)},${y(Math.max(v0, 0)).toFixed(1)}Z`;
  const last = pts[pts.length - 1];
  return `<svg class="em-line" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(label)}">
    <defs><linearGradient id="em-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stop-color="var(--brass)" stop-opacity=".35"/><stop offset="1" stop-color="var(--brass)" stop-opacity="0"/></linearGradient></defs>
    ${ticks.map((t) => `<line x1="${L}" x2="${W - R}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" class="em-grid"/><text x="${L - 6}" y="${(y(t) + 4).toFixed(1)}" text-anchor="end" class="em-axis">${fmt(t)}</text>`).join("")}
    <text x="${L}" y="${H - 6}" class="em-axis">M${m0}</text><text x="${W - R}" y="${H - 6}" text-anchor="end" class="em-axis">M${m1}</text>
    <path d="${area}" fill="url(#em-fill)"/>
    ${compare && compare.length > 1 ? `<path d="${path(compare)}" class="em-compare"/>` : ""}
    <path d="${path(pts)}" class="em-path"/>
    ${markers.map((mk) => `<g class="em-marker" data-month="${mk.month}"><line x1="${x(mk.month).toFixed(1)}" x2="${x(mk.month).toFixed(1)}" y1="${T}" y2="${H - B}"/><text x="${x(mk.month).toFixed(1)}" y="${T + 10}" text-anchor="middle">${esc(mk.icon || "•")}</text><title>M${mk.month}: ${esc(mk.label)}</title></g>`).join("")}
    <circle cx="${x(last.month).toFixed(1)}" cy="${y(last.value).toFixed(1)}" r="4.5" class="em-dot"/>
  </svg>${compare && compareLabel ? `<div class="em-legend"><span class="em-k now">${esc(label)}</span><span class="em-k was">${esc(compareLabel)}</span></div>` : ""}`;
}

/** A 0-100 meter with labelled lines. */
function emMeter(value, { lines = [], label = "", invert = false } = {}) {
  const W = 640, x = (v) => 16 + ((W - 32) * Math.max(0, Math.min(100, v))) / 100;
  return `<svg class="em-meter" viewBox="0 0 ${W} 74" role="img" aria-label="${esc(label)} ${Math.round(value)} of 100">
    <defs><linearGradient id="em-heat${invert ? "i" : ""}" x1="0" x2="1">${invert
      ? `<stop offset="0" stop-color="#fb7185"/><stop offset=".5" stop-color="#fbbf24"/><stop offset="1" stop-color="#34d399"/>`
      : `<stop offset="0" stop-color="#34d399"/><stop offset=".5" stop-color="#fbbf24"/><stop offset="1" stop-color="#fb7185"/>`}</linearGradient></defs>
    <rect x="16" y="26" width="${W - 32}" height="16" rx="8" class="nm-track"/>
    <rect x="16" y="26" width="${Math.max(6, x(value) - 16).toFixed(1)}" height="16" rx="8" fill="url(#em-heat${invert ? "i" : ""})"/>
    ${lines.map((l) => `<line x1="${x(l.at)}" x2="${x(l.at)}" y1="20" y2="48" class="nm-mark${value >= l.at ? " on" : ""}"/><text x="${x(l.at)}" y="14" text-anchor="middle" class="nm-mark-t${value >= l.at ? " on" : ""}">${esc(l.label)}</text>`).join("")}
    <circle cx="${x(value).toFixed(1)}" cy="34" r="11" class="nm-knob"/><text x="${x(value).toFixed(1)}" y="38" text-anchor="middle" class="nm-knob-t">${Math.round(value)}</text>
  </svg>`;
}

/** A labelled slider plus a centred commit button. */
function emSlider(id, label, { min, max, step = 1, value, unit = "money", button, hint = "" }) {
  const v = Math.min(max, Math.max(min, value));
  const show = (n) => (unit === "money" ? uiMoney(n) : unit === "pct" ? `${n}%` : `${n}`);
  return `<div class="em-slider">
    <label for="${id}">${esc(label)} <b id="${id}-val">${esc(show(v))}</b></label>
    <input type="range" id="${id}" min="${min}" max="${max}" step="${step}" value="${v}" data-em-unit="${unit}">
    ${hint ? `<div class="sx-card-sub" id="${id}-hint">${hint}</div>` : ""}
    ${button ? `<div class="em-actions"><button type="button" class="primary" data-em-go="${id}">${esc(button)}</button></div>` : ""}
  </div>`;
}

/** Keep every emSlider's readout live; onInput(id, value) for custom effect text. */
function emWireSliders(root, onInput) {
  root.querySelectorAll(".em-slider input[type=range]").forEach((r) => {
    const out = root.querySelector(`#${r.id}-val`);
    const upd = () => {
      const n = +r.value, u = r.dataset.emUnit;
      if (out) out.textContent = u === "money" ? uiMoney(n) : u === "pct" ? `${n}%` : `${n}`;
      if (onInput) onInput(r.id, n);
    };
    r.addEventListener("input", upd);
    upd();
  });
}

function emSliderValue(root, id) {
  const r = root.querySelector(`#${id}`);
  return r ? +r.value : 0;
}

// ---------------------------------------------------------------- tab extras (release 1.7)

const EM_EXTRAS = {};

/**
 * Add a panel to an existing tab. render(host, snap) fills it and returns
 * { title, summary, open } (open: start expanded, e.g. a letter is waiting).
 * Panels sit under the tab's header; `bottom` puts them at the end instead
 * (Products: the launch button stays first).
 */
function emRegister(tab, render, { bottom = false, into = null } = {}) {
  (EM_EXTRAS[tab] = EM_EXTRAS[tab] || []).push({ render, bottom, into });
}

async function emTabExtras(tab, body, snap) {
  // Every panel's box goes in first, in the same order as before; then they all load at once
  // (one after another, a tab with three panels waited for three round trips in a row).
  const anchor = body.querySelector(".sx-header, .panel-title");
  const jobs = (EM_EXTRAS[tab] || []).map((x) => {
    const box = document.createElement("details");
    box.className = "em-moves";
    box.hidden = true; // shown when its panel is ready, so nothing jumps around while loading
    const content = document.createElement("div");
    content.className = "em-moves-body";
    const inside = x.into && body.querySelector(x.into); // a page with sub-tabs (Deals) takes a panel into one of them
    if (inside) inside.appendChild(box);
    else if (x.bottom || !anchor) body.appendChild(box);
    else anchor.insertAdjacentElement("afterend", box);
    return { x, box, content };
  });
  await Promise.all(jobs.map(async ({ x, box, content }) => {
    let meta;
    try { meta = await x.render(content, snap); } catch (e) { box.remove(); console.warn(`${tab} extras:`, e); return; }
    if (!meta) { box.remove(); return; }
    box.innerHTML = `<summary><span class="em-moves-t">🧰 ${esc(meta.title)}</span><span class="em-moves-s">${esc(meta.summary || "")}</span>${meta.badge ? `<i class="em-moves-badge">${esc(meta.badge)}</i>` : ""}</summary>`;
    box.appendChild(content);
    if (meta.open) box.open = true;
    box.hidden = false;
  }));
}

window.gesTabExtras = emTabExtras;

export { EM_HOME, emAct, emRegister, emTabExtras, emLetters, emLine, emLog, emMeter, emSlider, emSliderValue, emWireLetters, emWireSliders };
